import type { IContact } from "@bbj-types/contact";
import type { IEnterprise } from "@bbj-types/enterprise";
import type { IJob } from "@bbj-types/job";
import type { IOffer } from "@bbj-types/offer";
import type { ObjectId } from "@bbj-types/utils";
import type React from "react";
import { type Dispatch, type SetStateAction, createContext, useContext, useMemo } from "react";
import type { IModalOfferDisableFields } from ".";
import { type IApplyCreate, initApply } from "./logics";

export type IEnterpriseBody = Pick<IEnterprise, "_id" | "name" | "contacts">;

export type TModalOfferMode = "create" | "editApply";

interface IModalOfferContext {
  onSubmit: () => void;
  onCancel: () => void;
  offer: Partial<IOffer>;
  setOffer: Dispatch<SetStateAction<Partial<IOffer>>>;
  createOffer: boolean;
  setCreateOffer: Dispatch<SetStateAction<boolean>>;
  enterprise: IEnterpriseBody | null;
  setEnterprise: Dispatch<SetStateAction<IEnterpriseBody | null>>;
  isSharedEnterprise: boolean;
  setIsSharedEnterprise: Dispatch<SetStateAction<boolean>>;
  contact: IContact | null;
  setContact: Dispatch<SetStateAction<IContact | null>>;
  setErrors: Dispatch<SetStateAction<Record<string, boolean>>>;
  getOfferUrl: (url: string) => void;
  cancelGetOfferUrl: () => void;
  errors: Record<string, boolean>;
  loadingUrl: boolean;
  searchUrlDone: boolean;
  setSearchUrlDone: Dispatch<SetStateAction<boolean>>;
  loading: boolean;
  disabled: boolean;
  candidateId?: ObjectId;
  apply: IApplyCreate;
  setApply: Dispatch<SetStateAction<IApplyCreate>>;
  followUpDate: Date | null;
  setFollowUpDate: Dispatch<SetStateAction<Date | null>>;
  appellation: IJob | null;
  setAppellation: Dispatch<SetStateAction<IJob | null>>;
  applyFollowUp: boolean;
  disabledFields?: IModalOfferDisableFields;
  mode: TModalOfferMode;
}

interface IModalOfferProviderProps extends IModalOfferContext {
  children: React.ReactNode;
}

const initialState: IModalOfferContext = {
  onSubmit: () => undefined,
  onCancel: () => undefined,
  offer: {},
  setOffer: () => undefined,
  createOffer: false,
  setCreateOffer: () => undefined,
  enterprise: { _id: "", name: "", contacts: [] },
  setEnterprise: () => undefined,
  isSharedEnterprise: false,
  setIsSharedEnterprise: () => undefined,
  contact: null,
  setContact: () => undefined,
  errors: {},
  setErrors: () => undefined,
  getOfferUrl: () => undefined,
  cancelGetOfferUrl: () => undefined,
  candidateId: undefined,
  disabled: false,
  searchUrlDone: false,
  setSearchUrlDone: () => undefined,
  loadingUrl: false,
  loading: false,
  apply: initApply,
  setApply: () => undefined,
  followUpDate: null,
  setFollowUpDate: () => undefined,
  appellation: null,
  setAppellation: () => undefined,
  applyFollowUp: false,
  mode: "create",
};

const context = createContext(initialState);

export const useModalOfferContext = () => useContext(context);

export const ModalOfferProvider = ({ children, ...props }: IModalOfferProviderProps) => {
  const value = useMemo(
    () => ({ ...props }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      props.onSubmit,
      props.onCancel,
      props.offer,
      props.setOffer,
      props.createOffer,
      props.setCreateOffer,
      props.enterprise,
      props.setEnterprise,
      props.isSharedEnterprise,
      props.setIsSharedEnterprise,
      props.contact,
      props.setContact,
      props.errors,
      props.setErrors,
      props.getOfferUrl,
      props.cancelGetOfferUrl,
      props.candidateId,
      props.disabled,
      props.searchUrlDone,
      props.setSearchUrlDone,
      props.loadingUrl,
      props.loading,
      props.apply,
      props.setApply,
      props.followUpDate,
      props.setFollowUpDate,
      props.appellation,
      props.setAppellation,
      props.applyFollowUp,
      props.mode,
    ]
  );

  return <context.Provider value={value}>{children}</context.Provider>;
};
